/** Shared helpers for event create / edit forms. */

import type { EventListItem } from "../types";

export type TicketTierDraft = {
  /** Client-side row key */
  id: string;
  /** Present when tier already exists on the server (edit flow) */
  serverId?: string;
  tier_name: string;
  price: string;
  quantity: string;
  sold?: number;
};

export type NormalizedTicketTier = {
  tier_name: string;
  price: number;
  quantity: number;
};

export function toIsoOrUndefined(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

/** `datetime-local` value from an ISO timestamp. */
export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** `date` input value from API `event_date` (date-only or ISO). */
export function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function normalizeTicketTiers(tiers: TicketTierDraft[]): NormalizedTicketTier[] | null {
  const normalized = tiers
    .map((tier) => ({
      tier_name: tier.tier_name.trim(),
      price: Number(tier.price),
      quantity: Number(tier.quantity),
    }))
    .filter(
      (tier) => tier.tier_name && Number.isFinite(tier.price) && Number.isFinite(tier.quantity) && tier.quantity > 0,
    );

  if (normalized.length === 0) return null;
  return normalized;
}

/** Sum of all positive ticket-tier quantities currently entered in the form. */
export function totalTierQuantity(tiers: Pick<TicketTierDraft, "quantity">[]): number {
  return tiers.reduce((sum, tier) => {
    const quantity = Number(tier.quantity);
    return Number.isFinite(quantity) && quantity > 0 ? sum + quantity : sum;
  }, 0);
}

/** Parsed venue capacity, or null when the field is empty / not a positive number. */
export function parseVenueCapacity(capacity: string): number | null {
  const value = Number(capacity);
  return capacity.trim() !== "" && Number.isFinite(value) && value > 0 ? value : null;
}

const formatCount = (n: number) => n.toLocaleString("en-IN");

export function ticketCapacityMessage(total: number, capacity: number): string {
  return `Ticket tiers add up to ${formatCount(total)}, which is more than the venue capacity of ${formatCount(capacity)}.`;
}

/** Error text when tier quantities exceed venue capacity, else null. */
export function ticketCapacityError(tiers: TicketTierDraft[], capacityInput: string): string | null {
  const capacity = parseVenueCapacity(capacityInput);
  if (capacity === null) return null;
  const total = totalTierQuantity(tiers);
  return total > capacity ? ticketCapacityMessage(total, capacity) : null;
}

export function tierBelowSoldMessage(tierName: string, sold: number): string {
  const name = tierName.trim() || "This tier";
  return `"${name}" has already sold ${formatCount(sold)} ticket${sold === 1 ? "" : "s"}, so its quantity cannot be lower than that.`;
}

/** First existing tier whose quantity was lowered below its sold count. */
export function findTierBelowSold(tiers: TicketTierDraft[]): TicketTierDraft | null {
  return tiers.find((tier) => tier.serverId && (tier.sold ?? 0) > 0 && Number(tier.quantity) < (tier.sold ?? 0)) ?? null;
}

const CANCELLED_STATUSES = new Set(["cancelled", "canceled"]);

export function isCancelledStatus(status: string | null | undefined): boolean {
  return CANCELLED_STATUSES.has((status ?? "").trim().toLowerCase());
}

type VenueSlot = {
  venueId: string;
  eventDate: string;
  /** ISO timestamp or `datetime-local` value */
  startAt?: string | null;
  endAt?: string | null;
  excludeEventId?: string;
};

function validInterval(start?: string | null, end?: string | null): [number, number] | null {
  if (!start || !end) return null;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  return Number.isFinite(s) && Number.isFinite(e) && e > s ? [s, e] : null;
}

/**
 * Client-side double-booking guard: an event at the same venue that overlaps
 * the given slot. Compares [start, end) when both sides have timestamps,
 * otherwise falls back to the same calendar day. Cancelled events never block.
 */
export function findVenueConflict(events: EventListItem[], slot: VenueSlot): EventListItem | null {
  if (!slot.venueId) return null;
  const mine = validInterval(slot.startAt, slot.endAt);
  return (
    events.find((event) => {
      if (event.id === slot.excludeEventId) return false;
      if (event.venue_id !== slot.venueId) return false;
      if (isCancelledStatus(event.status)) return false;
      const theirs = validInterval(event.start_date, event.end_date);
      if (mine && theirs) return mine[0] < theirs[1] && theirs[0] < mine[1];
      return isoToDateInput(event.event_date) === slot.eventDate;
    }) ?? null
  );
}

export function venueConflictMessage(conflict: EventListItem): string {
  const key = isoToDateInput(conflict.event_date);
  const [y, m, d] = key.split("-").map(Number);
  const date = key
    ? new Date(y, m - 1, d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "the same day";
  return `"${conflict.name}" is already booked at this venue on ${date}.`;
}

export const EVENT_STATUS_OPTIONS =["draft", "planned", "on_sale", "active", "completed", "cancelled"] as const;
